import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { MessageSquareWarning } from "lucide-react";
import { db } from "../../firebase/config";
import { useCmsAuth } from "../../context/CmsAuthContext";
import { Field, SaveBar, inputClass, textareaClass } from "../cmsApi.jsx";
import {
  QUEJA_ESTADOS,
  QUEJA_ORIGEN,
  QUEJA_TIPOS,
  createEmptyQueja,
  estadoLabel,
  isAnalisisOverdue,
} from "../../lib/quejasSchema";
import { LABSYSO_FEEDBACK } from "../../config/labsysoFeedback";

export default function CmsQuejasEditor() {
  const { user, profile, hasModule } = useCmsAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(createEmptyQueja());
  const [filterTipo, setFilterTipo] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [search, setSearch] = useState("");
  const [notaNueva, setNotaNueva] = useState("");
  const [manualMode, setManualMode] = useState(false);

  const canAccess = hasModule("quejas");
  const canDelete = profile?.role === "owner" || profile?.role === "editor";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(query(collection(db, "quejas"), orderBy("createdAt", "desc")));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err?.message || "No se pudieron cargar las quejas.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) load();
  }, [canAccess, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((row) => {
      if (filterTipo && row.tipoSolicitud !== filterTipo) return false;
      if (filterEstado && row.estado !== filterEstado) return false;
      if (!q) return true;
      const blob = `${row.nombreCompleto || ""} ${row.email || ""} ${row.folio || ""} ${row.descripcion || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [items, filterTipo, filterEstado, search]);

  const onSelect = (row) => {
    setSelectedId(row.id);
    setManualMode(false);
    setForm(
      createEmptyQueja({
        ...row,
        notasInternas: Array.isArray(row.notasInternas) ? row.notasInternas : [],
      })
    );
    setMessage("");
    setError("");
    setNotaNueva("");
  };

  const onNewManual = () => {
    setSelectedId(null);
    setManualMode(true);
    setForm(
      createEmptyQueja({
        source: QUEJA_ORIGEN.MANUAL,
        estado: "recibida",
        fechaRecepcion: new Date().toISOString().slice(0, 10),
        asignadoEmail: user?.email || profile?.email || "",
      })
    );
    setMessage("");
    setError("");
    setNotaNueva("");
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (!form.nombreCompleto?.trim() || !String(form.email || "").includes("@")) {
        throw new Error("Nombre y email del solicitante son requeridos.");
      }
      if (!form.descripcion?.trim()) {
        throw new Error("Descripción requerida.");
      }

      const notas = Array.isArray(form.notasInternas) ? [...form.notasInternas] : [];
      if (notaNueva.trim()) {
        notas.push({
          texto: notaNueva.trim(),
          autor: user?.email || profile?.email || "",
          fecha: new Date().toISOString(),
        });
      }

      const payload = {
        ...createEmptyQueja(form),
        notasInternas: notas,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      };
      delete payload.id;

      if (selectedId) {
        await updateDoc(doc(db, "quejas", selectedId), payload);
        setMessage("Seguimiento guardado.");
        setNotaNueva("");
      } else {
        const folio =
          form.folio?.trim() ||
          `M-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${Math.floor(
            1000 + Math.random() * 9000
          )}`;
        const ref = await addDoc(collection(db, "quejas"), {
          ...payload,
          folio,
          codigoInterno: form.codigoInterno || folio,
          source: QUEJA_ORIGEN.MANUAL,
          createdAt: serverTimestamp(),
        });
        setSelectedId(ref.id);
        setManualMode(false);
        setMessage("Queja registrada manualmente.");
        setNotaNueva("");
      }
      await load();
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!selectedId || !canDelete) return;
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try {
      await deleteDoc(doc(db, "quejas", selectedId));
      setSelectedId(null);
      setManualMode(false);
      setForm(createEmptyQueja());
      setMessage("Eliminada.");
      await load();
    } catch (err) {
      setError(err?.message || "No se pudo eliminar.");
    }
  };

  if (!canAccess) {
    return (
      <p className="text-sm text-brand-dark/60">Sin acceso al módulo de quejas.</p>
    );
  }

  const showDetail = selectedId || manualMode;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-black">Quejas / sugerencias</h2>
          <p className="text-sm text-brand-dark/55 mt-1 max-w-2xl">
            Buzón LABSYSO ({LABSYSO_FEEDBACK.formCode}). Listado y seguimiento F-PGC-6-2 en esta
            misma página. Plazo análisis: {LABSYSO_FEEDBACK.analysisDeadlineDays} días hábiles.
          </p>
        </div>
        <button
          type="button"
          onClick={onNewManual}
          className="bg-brand-primary text-white px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-widest"
        >
          Alta manual
        </button>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <MessageSquareWarning size={18} className="text-brand-primary" />
            Listado ({filtered.length})
          </h3>
          <button
            type="button"
            onClick={load}
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
          >
            Actualizar
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <input
            className={inputClass}
            placeholder="Buscar nombre, email, folio…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={inputClass}
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
          >
            <option value="">Todos los tipos</option>
            <option value={QUEJA_TIPOS.QUEJA}>Queja / reclamo</option>
            <option value={QUEJA_TIPOS.SUGERENCIA}>Sugerencia</option>
          </select>
          <select
            className={inputClass}
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            {QUEJA_ESTADOS.map((e) => (
              <option key={e.id} value={e.id}>
                {e.label}
              </option>
            ))}
          </select>
        </div>

        {error && !showDetail && <p className="text-sm text-red-600">{error}</p>}
        {loading && <p className="text-sm text-brand-dark/50">Cargando…</p>}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-brand-dark/50">No hay solicitudes con estos filtros.</p>
        )}

        <ul className="divide-y divide-brand-soft max-h-[28rem] overflow-y-auto">
          {filtered.map((row) => {
            const overdue = isAnalisisOverdue(row, LABSYSO_FEEDBACK.analysisDeadlineDays);
            const active = selectedId === row.id;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => onSelect(row)}
                  className={`w-full text-left py-3 px-2 rounded-xl transition-colors ${
                    active ? "bg-brand-soft/60" : "hover:bg-[#f8faf9]"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-bold text-brand-dark">
                      {row.folio || row.gasSolicitudId || row.id.slice(0, 8)}
                      <span className="font-medium text-brand-dark/50">
                        {" "}
                        · {row.tipoSolicitud === "sugerencia" ? "Sugerencia" : "Queja"}
                      </span>
                    </p>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-lg ${
                        overdue
                          ? "bg-red-100 text-red-700"
                          : "bg-brand-primary/10 text-brand-primary"
                      }`}
                    >
                      {estadoLabel(row.estado)}
                      {overdue ? " · plazo" : ""}
                    </span>
                  </div>
                  <p className="text-sm text-brand-dark/70 mt-1 truncate">
                    {row.nombreCompleto} · {row.email}
                  </p>
                  <p className="text-xs text-brand-dark/45 mt-0.5 line-clamp-1">
                    {row.descripcion}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {showDetail && (
        <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-bold text-lg">
              {manualMode ? "Alta manual" : "Detalle y seguimiento F-PGC-6-2"}
            </h3>
            {selectedId && canDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="text-xs font-bold uppercase tracking-widest text-red-600"
              >
                Eliminar
              </button>
            )}
          </div>

          {message && <p className="text-sm text-brand-primary font-medium">{message}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="rounded-2xl bg-[#f8faf9] border border-brand-soft p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
              Solicitud (F-PGC-6-1)
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tipo">
                <select
                  className={inputClass}
                  value={form.tipoSolicitud}
                  onChange={(e) => setField("tipoSolicitud", e.target.value)}
                  disabled={!!selectedId && form.source === QUEJA_ORIGEN.WEB_LABSYSO}
                >
                  <option value={QUEJA_TIPOS.QUEJA}>Queja / reclamo</option>
                  <option value={QUEJA_TIPOS.SUGERENCIA}>Sugerencia</option>
                </select>
              </Field>
              <Field label="Folio">
                <input
                  className={inputClass}
                  value={form.folio || ""}
                  onChange={(e) => setField("folio", e.target.value)}
                />
              </Field>
              <Field label="Nombre">
                <input
                  className={inputClass}
                  value={form.nombreCompleto}
                  onChange={(e) => setField("nombreCompleto", e.target.value)}
                />
              </Field>
              <Field label="Email">
                <input
                  className={inputClass}
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                />
              </Field>
              <Field label="Teléfono">
                <input
                  className={inputClass}
                  value={form.telefono}
                  onChange={(e) => setField("telefono", e.target.value)}
                />
              </Field>
              <Field label="Relación">
                <input
                  className={inputClass}
                  value={form.relacion}
                  onChange={(e) => setField("relacion", e.target.value)}
                />
              </Field>
              {form.tipoSolicitud === QUEJA_TIPOS.QUEJA && (
                <>
                  <Field label="Fecha incidente">
                    <input
                      className={inputClass}
                      type="date"
                      value={form.fechaIncidente || ""}
                      onChange={(e) => setField("fechaIncidente", e.target.value)}
                    />
                  </Field>
                  <Field label="Área">
                    <input
                      className={inputClass}
                      value={form.areaServicio || ""}
                      onChange={(e) => setField("areaServicio", e.target.value)}
                    />
                  </Field>
                </>
              )}
            </div>
            <Field label="Descripción">
              <textarea
                className={textareaClass}
                rows={4}
                value={form.descripcion}
                onChange={(e) => setField("descripcion", e.target.value)}
              />
            </Field>
          </div>

          <div className="space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
              Seguimiento (F-PGC-6-2)
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Estado">
                <select
                  className={inputClass}
                  value={form.estado}
                  onChange={(e) => setField("estado", e.target.value)}
                >
                  {QUEJA_ESTADOS.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Fecha recepción">
                <input
                  className={inputClass}
                  type="date"
                  value={form.fechaRecepcion || ""}
                  onChange={(e) => setField("fechaRecepcion", e.target.value)}
                />
              </Field>
              <Field label="Acuse enviado">
                <label className="flex items-center gap-2 text-sm py-3">
                  <input
                    type="checkbox"
                    checked={!!form.acuseEnviado}
                    onChange={(e) => setField("acuseEnviado", e.target.checked)}
                  />
                  Sí
                </label>
              </Field>
              <Field label="Fecha acuse">
                <input
                  className={inputClass}
                  type="date"
                  value={form.fechaAcuse || ""}
                  onChange={(e) => setField("fechaAcuse", e.target.value)}
                />
              </Field>
              <Field label="¿Procede?">
                <select
                  className={inputClass}
                  value={
                    form.procede === true ? "si" : form.procede === false ? "no" : ""
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setField("procede", v === "si" ? true : v === "no" ? false : null);
                  }}
                >
                  <option value="">Sin definir</option>
                  <option value="si">Procede</option>
                  <option value="no">No procede</option>
                </select>
              </Field>
              <Field label="Límite análisis">
                <input
                  className={inputClass}
                  type="date"
                  value={form.fechaLimiteAnalisis || ""}
                  onChange={(e) => setField("fechaLimiteAnalisis", e.target.value)}
                />
              </Field>
              <Field label="Asignado (email)">
                <input
                  className={inputClass}
                  value={form.asignadoEmail || ""}
                  onChange={(e) => setField("asignadoEmail", e.target.value)}
                />
              </Field>
              <Field label="Fecha comunicación">
                <input
                  className={inputClass}
                  type="date"
                  value={form.fechaComunicacion || ""}
                  onChange={(e) => setField("fechaComunicacion", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Análisis / verificación">
              <textarea
                className={textareaClass}
                rows={3}
                value={form.analisisResumen || ""}
                onChange={(e) => setField("analisisResumen", e.target.value)}
              />
            </Field>
            <Field label="Solución inmediata">
              <textarea
                className={textareaClass}
                rows={3}
                value={form.solucionInmediata || ""}
                onChange={(e) => setField("solucionInmediata", e.target.value)}
              />
            </Field>
            <Field label="Comunicación al reclamante">
              <textarea
                className={textareaClass}
                rows={3}
                value={form.comunicacionCliente || ""}
                onChange={(e) => setField("comunicacionCliente", e.target.value)}
              />
            </Field>
            <Field label="Acciones correctivas / PGC-7">
              <textarea
                className={textareaClass}
                rows={3}
                value={form.accionesCorrectivas || ""}
                onChange={(e) => setField("accionesCorrectivas", e.target.value)}
              />
            </Field>
            <Field label="Evaluación de efectividad">
              <textarea
                className={textareaClass}
                rows={3}
                value={form.evaluacionEfectividad || ""}
                onChange={(e) => setField("evaluacionEfectividad", e.target.value)}
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Fecha cierre">
                <input
                  className={inputClass}
                  type="date"
                  value={form.fechaCierre || ""}
                  onChange={(e) => setField("fechaCierre", e.target.value)}
                />
              </Field>
              <Field label="Cerrado por">
                <input
                  className={inputClass}
                  value={form.cerradoPor || ""}
                  onChange={(e) => setField("cerradoPor", e.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
              Bitácora interna
            </p>
            <ul className="space-y-2 max-h-40 overflow-y-auto">
              {(form.notasInternas || []).length === 0 && (
                <li className="text-sm text-brand-dark/45">Sin notas.</li>
              )}
              {(form.notasInternas || []).map((n, i) => (
                <li
                  key={`${n.fecha}-${i}`}
                  className="text-sm border border-brand-soft rounded-xl px-3 py-2"
                >
                  <span className="text-[10px] text-brand-dark/45">
                    {n.fecha?.slice?.(0, 16) || ""} · {n.autor}
                  </span>
                  <p className="mt-1">{n.texto}</p>
                </li>
              ))}
            </ul>
            <Field label="Nueva nota">
              <textarea
                className={textareaClass}
                rows={2}
                value={notaNueva}
                onChange={(e) => setNotaNueva(e.target.value)}
                placeholder="Se guarda al pulsar Guardar"
              />
            </Field>
          </div>

          <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
        </section>
      )}
    </div>
  );
}
